import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { appointmentsQueryKey } from './appointment-list-keys';
import type {
  AppointmentsDetailsResponse,
  AppointmentsRequest,
  DateFilter,
} from '@/types/scheduling-api';

/**
 * POST /api/Appointment — full appointment list for a date range, used to
 * draw the calendar grid in Day/Week/Month views. The slot endpoint
 * (calendarSlots) is for the provider-strip enrichment, NOT the grid.
 *
 * Body shape mirrors Flutter's working dev curl: includes practitioners,
 * timezone, and the Flutter-specific flags (pageNumber: -1 means "no paging").
 *
 * PRACTITIONERS ONLY. Device sessions have their own route and their own hook
 * since PROD-2382 — `useDeviceAppointments`. They used to ride in
 * `practitioners` here, which the backend tolerated only because its
 * participant filter never checked the type.
 */

interface AppointmentsParams {
  organizationId: string;
  practiceId: string;
  startDate: string;
  endDate: string;
  filter: DateFilter;
  practitioners?: string[];
  searchText?: string;
  status?: AppointmentsRequest['status'];
  location?: string[];
  types?: AppointmentsRequest['types'];
  pageNumber?: number;
  pageSize?: number;
  isTimeSelected?: boolean;
  showActiveAppointmentOnly?: boolean;
}

interface AppointmentsOptions {
  /**
   * Keep the PREVIOUS key's appointments on screen while a new key loads,
   * instead of dropping to `pending` (and, downstream, to a spinner). Opt-in
   * per caller, because it is only right where the params change UNDER a
   * rendered list — the Technician Worklist's key is derived from the device
   * ids and today's date, so adding a device or crossing midnight remints it.
   * The calendar grid changes key on a deliberate navigation, where showing
   * the previous day's tiles during the fetch would be a lie about the date.
   */
  keepPreviousData?: boolean;
}

export function useAppointments(
  params: AppointmentsParams | null,
  options: AppointmentsOptions = {},
) {
  return useQuery({
    // `['appointments', params]`, built rather than spelled out so this key and
    // the mutation hooks that patch it cannot drift apart (PROD-2382 added a
    // second list key; see `appointment-list-keys`). The VALUE is unchanged.
    queryKey: appointmentsQueryKey(params),
    queryFn: ({ signal }) => {
      if (!params) throw new Error('useAppointments called without params');
      const body: AppointmentsRequest = {
        searchText: params.searchText ?? '',
        LoggedInUserTimezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        filter: params.filter,
        startDate: params.startDate,
        endDate: params.endDate,
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        practitioners: params.practitioners ?? [],
        status: params.status ?? [],
        location: params.location ?? [],
        types: params.types ?? [],
        pageNumber: params.pageNumber ?? -1,
        pageSize: params.pageSize ?? 10,
        isTimeSelected: params.isTimeSelected ?? false,
        showActiveAppointmentOnly: params.showActiveAppointmentOnly ?? false,
      };
      return post<AppointmentsDetailsResponse>('/api/Appointment', body, { signal });
    },
    enabled: !!params?.practiceId,
    refetchOnMount: 'always',
    // `undefined` when not opted in, which is exactly "no placeholder" — React
    // Query only consults `placeholderData` when it is not `undefined`.
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined,
  });
}
